/**
 * DataTable — renders a static array of data with @tanstack/react-table.
 * For server-side paginated data use PaginatedTable instead.
 */
import {
  Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow,
  Input, makeStyles, Text, tokens,
} from "@fluentui/react-components";
import { SearchRegular } from "@fluentui/react-icons";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import { useState } from "react";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalS },
  table: { width: "100%" },
  headerCell: { backgroundColor: tokens.colorNeutralBackground2, fontWeight: tokens.fontWeightSemibold },
  emptyRow: { textAlign: "center", padding: tokens.spacingVerticalXL, color: tokens.colorNeutralForeground3 },
});

interface Props<TData> {
  columns: ColumnDef<TData, unknown>[];
  data: TData[];
  enableSearch?: boolean;
  enablePagination?: boolean;
  pageSize?: number;
}

export function DataTable<TData>({ columns, data, enableSearch = true, pageSize = 10 }: Props<TData>): React.JSX.Element {
  const [globalFilter, setGlobalFilter] = useState("");
  const s = useStyles();

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: { globalFilter, pagination: { pageIndex: 0, pageSize } },
    onGlobalFilterChange: setGlobalFilter,
  });

  return (
    <div className={s.root}>
      {enableSearch && (
        <Input
          contentBefore={<SearchRegular />}
          placeholder="Search…"
          value={globalFilter}
          onChange={(_, d) => setGlobalFilter(d.value)}
          style={{ maxWidth: "24rem" }}
        />
      )}
      <Table className={s.table}>
        <TableHeader>
          {table.getHeaderGroups().map((hg) => (
            <TableRow key={hg.id}>
              {hg.headers.map((h) => (
                <TableHeaderCell key={h.id} className={s.headerCell}>
                  {flexRender(h.column.columnDef.header, h.getContext())}
                </TableHeaderCell>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length === 0 ? (
            <TableRow><TableCell colSpan={columns.length} className={s.emptyRow}><Text>No data</Text></TableCell></TableRow>
          ) : (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
